/**
 * PublicBooking.tsx — Public venue booking portal
 * Routes served at /book/*
 * Design: light theme matching Bookable council sites
 */
import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Routes, Route, useNavigate, useParams, useLocation } from 'react-router-dom';

const API = '/api';

// ── Types ─────────────────────────────────────────────────────────────────────
interface Space {
  id: string;
  name: string;
  description?: string;
  category?: string;
  amenities?: string[];
  hourly_rate?: number | null;
  daily_rate?: number | null;
  capacity?: number | null;
  location?: string;
  image_url?: string;
  min_duration_hours?: number;
  max_duration_hours?: number;
  booking_lead_days?: number;
}

interface TimeSlot {
  start: string;
  end: string;
  available: boolean;
}

interface PubUser {
  id: string;
  name: string;
  email: string;
  token: string;
}

// ── Public Auth Context ───────────────────────────────────────────────────────
interface PubAuthCtx {
  user: PubUser | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}
const PubAuth = createContext<PubAuthCtx>({ user: null, login: async () => {}, logout: () => {} });

function PubAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<PubUser | null>(() => {
    try { return JSON.parse(localStorage.getItem('lemai_pub_user') || 'null'); } catch { return null; }
  });

  const login = async (email: string, password: string) => {
    const res = await fetch(`${API}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) throw new Error('Invalid email or password');
    const data = await res.json();
    const u: PubUser = {
      id: data.user?.id || '',
      name: data.user?.name || email.split('@')[0],
      email: data.user?.email || email,
      token: data.access_token,
    };
    setUser(u);
    localStorage.setItem('lemai_pub_user', JSON.stringify(u));
    localStorage.setItem('lemai_pub_tok', u.token);
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('lemai_pub_user');
    localStorage.removeItem('lemai_pub_tok');
  };

  return <PubAuth.Provider value={{ user, login, logout }}>{children}</PubAuth.Provider>;
}

// ── Shared header/footer ──────────────────────────────────────────────────────
function PortalHeader() {
  const { user, logout } = useContext(PubAuth);
  const nav = useNavigate();
  return (
    <header style={{ background: '#0d0d0d', color: '#e5e5e5', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 46, borderBottom: '1px solid #1a1a1a', position: 'sticky', top: 0, zIndex: 100 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }} onClick={() => nav('/book')}>
        <div style={{ width: 28, height: 28, background: '#1a1a1a', border: '1px solid #333', borderRadius: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>🏛️</div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, lineHeight: 1.2 }}>Venue Bookings</div>
          <div style={{ fontSize: 11, opacity: 0.8 }}>Community spaces & facilities</div>
        </div>
      </div>
      <nav style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <NavLink to="/book">Browse Venues</NavLink>
        <NavLink to="/book/lookup">My Reference</NavLink>
        {user ? (
          <>
            <NavLink to="/book/my-bookings">My Bookings</NavLink>
            <button onClick={logout} style={{ marginLeft: 8, padding: '3px 10px', background: '#1a1a1a', color: '#888', border: '1px solid #333', borderRadius: 0, cursor: 'pointer', fontSize: 10, fontWeight: 700 }}>Sign Out</button>
          </>
        ) : (
          <button onClick={() => nav('/book/login')} style={{ marginLeft: 8, padding: '3px 10px', background: '#1e3a5f', color: '#60a5fa', border: '1px solid #1e3a5f', borderRadius: 0, cursor: 'pointer', fontSize: 10, fontWeight: 700 }}>Sign In</button>
        )}
      </nav>
    </header>
  );
}

function NavLink({ to, children }: { to: string; children: React.ReactNode }) {
  const nav = useNavigate();
  return (
    <a href={to} onClick={e => { e.preventDefault(); nav(to); }}
      style={{ color: '#555', textDecoration: 'none', fontSize: 10, fontWeight: 700, padding: '4px 8px', borderRadius: 0, transition: 'background 0.15s', letterSpacing: '0.05em', textTransform: 'uppercase' }}
      onMouseEnter={e => (e.currentTarget.style.background = '#1a1a1a')}
      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
    >{children}</a>
  );
}

function PortalFooter() {
  return (
    <footer style={{ background: '#0a0a0a', color: '#333', padding: '24px', marginTop: 'auto', textAlign: 'center', fontSize: 11, borderTop: '1px solid #1a1a1a' }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <div style={{ color: '#444', fontWeight: 700, marginBottom: 6, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Venue Booking Portal</div>
        <div style={{ marginBottom: 12 }}>Book community spaces and council facilities online</div>
        <div style={{ borderTop: '1px solid #1a1a1a', paddingTop: 12, fontSize: 10 }}>
          © {new Date().getFullYear()} Powered by Lemai — For booking assistance contact your facility manager
        </div>
      </div>
    </footer>
  );
}

// ── Utility components ────────────────────────────────────────────────────────
function Spinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
      <div style={{ width: 24, height: 24, border: '2px solid #222', borderTop: '2px solid #3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function Alert({ type, children }: { type: 'error' | 'info' | 'success'; children: React.ReactNode }) {
  const colors = { error: { bg: '#450a0a', border: '#7f1d1d', text: '#dc2626', icon: '⚠' }, info: { bg: '#0c1e3d', border: '#1e3a5f', text: '#60a5fa', icon: 'ℹ' }, success: { bg: '#052e16', border: '#14532d', text: '#16a34a', icon: '✓' } };
  const c = colors[type];
  return (
    <div style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 0, padding: '8px 12px', display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12 }}>
      <span style={{ color: c.text, fontWeight: 700, fontSize: 12 }}>{c.icon}</span>
      <div style={{ color: c.text, fontSize: 11 }}>{children}</div>
    </div>
  );
}

function Badge({ label, color = '#1a56db' }: { label: string; color?: string }) {
  return <span style={{ background: `${color}18`, color, border: `1px solid ${color}40`, borderRadius: 2, padding: '1px 6px', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>;
}

function Card({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <div style={{ background: '#111', borderRadius: 0, border: '1px solid #1a1a1a', ...style }}>{children}</div>;
}

// ── Category colors ───────────────────────────────────────────────────────────
const CAT_COLORS: Record<string, string> = {
  'Meeting Room': '#3b82f6', 'Community Hall': '#8b5cf6', 'Sports': '#f97316',
  'Outdoor': '#22c55e', 'Pavilion': '#06b6d4', 'Event Space': '#ec4899',
  'Recreation': '#f59e0b', 'Conference': '#6366f1',
};
function catColor(cat?: string) { return cat ? (CAT_COLORS[cat] || '#555555') : '#555555'; }

// ── Space card images (placeholder gradient) ──────────────────────────────────
const CAT_GRADIENTS: Record<string, string> = {
  'Meeting Room': 'linear-gradient(135deg,#dbeafe,#bfdbfe)',
  'Community Hall': 'linear-gradient(135deg,#ede9fe,#ddd6fe)',
  'Sports': 'linear-gradient(135deg,#ffedd5,#fed7aa)',
  'Outdoor': 'linear-gradient(135deg,#dcfce7,#bbf7d0)',
  'Pavilion': 'linear-gradient(135deg,#cffafe,#a5f3fc)',
  'Event Space': 'linear-gradient(135deg,#fce7f3,#fbcfe8)',
};
function catGradient(cat?: string) { return cat ? (CAT_GRADIENTS[cat] || 'linear-gradient(135deg,#f1f5f9,#e2e8f0)') : 'linear-gradient(135deg,#f1f5f9,#e2e8f0)'; }

// ── Category icons ────────────────────────────────────────────────────────────
const CAT_ICONS: Record<string, string> = {
  'Meeting Room': '🏢', 'Community Hall': '🏛️', 'Sports': '⚽', 'Outdoor': '🌳',
  'Pavilion': '⛺', 'Event Space': '🎪', 'Recreation': '🎾', 'Conference': '📊',
};
function catIcon(cat?: string) { return cat ? (CAT_ICONS[cat] || '📍') : '📍'; }

// ═════════════════════════════════════════════════════════════════════════════
// PAGE 1 — HOMEPAGE: venue grid + search + category filter
// ═════════════════════════════════════════════════════════════════════════════
function HomePage() {
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const nav = useNavigate();

  useEffect(() => {
    fetch(`${API}/public/bookings/spaces`)
      .then(r => r.json())
      .then(d => { setSpaces(d.spaces || []); setLoading(false); })
      .catch(() => { setError('Unable to load spaces'); setLoading(false); });
  }, []);

  const categories = Array.from(new Set(spaces.map(s => s.category).filter(Boolean))) as string[];

  const filtered = spaces.filter(s => {
    const q = search.toLowerCase();
    const matchQ = !q || s.name.toLowerCase().includes(q) || (s.description || '').toLowerCase().includes(q) || (s.location || '').toLowerCase().includes(q);
    const matchCat = !category || s.category === category;
    return matchQ && matchCat;
  });

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', flexDirection: 'column' }}>
      <PortalHeader />

      {/* Hero */}
      <div style={{ background: '#0d0d0d', color: '#e5e5e5', padding: '32px 24px', textAlign: 'center', borderBottom: '1px solid #1a1a1a' }}>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 8px', lineHeight: 1.2, letterSpacing: '-0.01em' }}>Book a Council Venue</h1>
          <p style={{ fontSize: 12, color: '#555', margin: '0 0 20px', lineHeight: 1.5 }}>Find and book community spaces, meeting rooms, and event facilities online</p>
          <div style={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: 0, padding: '0 12px', display: 'flex', gap: 8, maxWidth: 540, margin: '0 auto', height: 36, alignItems: 'center' }}>
            <span style={{ fontSize: 18, display: 'flex', alignItems: 'center', color: '#a0a0a0' }}>🔍</span>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search venues, facilities…"
              style={{ flex: 1, border: 'none', outline: 'none', fontSize: 12, color: '#e5e5e5', background: 'transparent' }}
            />
            {search && <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#a0a0a0', fontSize: 18 }}>✕</button>}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px', width: '100%', flex: 1 }}>
        {/* Category pills */}
        {categories.length > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 28 }}>
            <button onClick={() => setCategory('')} style={{ padding: '3px 10px', borderRadius: 0, border: `1px solid ${!category ? '#3b82f6' : '#333'}`, background: !category ? '#1e3a5f' : '#1a1a1a', color: !category ? '#60a5fa' : '#555', fontSize: 10, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s', textTransform: 'uppercase', letterSpacing: '0.04em' }}>All Venues</button>
            {categories.map(cat => (
              <button key={cat} onClick={() => setCategory(cat === category ? '' : cat)}
                style={{ padding: '3px 10px', borderRadius: 0, border: `1px solid ${category === cat ? catColor(cat) : '#333'}`, background: category === cat ? `${catColor(cat)}20` : '#1a1a1a', color: category === cat ? catColor(cat) : '#555', fontSize: 10, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {catIcon(cat)} {cat}
              </button>
            ))}
          </div>
        )}

        {loading && <Spinner />}
        {error && <Alert type="error">{error}</Alert>}

        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 0', color: '#333' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🔍</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#444' }}>No venues found</div>
            <div style={{ fontSize: 14, marginTop: 8 }}>Try a different search or category</div>
            <button onClick={() => { setSearch(''); setCategory(''); }} style={{ marginTop: 14, padding: '5px 14px', background: '#1e3a5f', color: '#60a5fa', border: '1px solid #1e3a5f', borderRadius: 0, cursor: 'pointer', fontSize: 10, fontWeight: 700 }}>Clear filters</button>
          </div>
        )}

        {/* Grid */}
        {!loading && filtered.length > 0 && (
          <>
            <div style={{ fontSize: 10, color: '#444', marginBottom: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{filtered.length} venue{filtered.length !== 1 ? 's' : ''} available</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 1, border: '1px solid #222', borderRight: 'none', borderBottom: 'none' }}>
              {filtered.map(space => (
                <VenueCard key={space.id} space={space} onClick={() => nav(`/book/venue/${space.id}`)} />
              ))}
            </div>
          </>
        )}
      </div>
      <PortalFooter />
    </div>
  );
}

function VenueCard({ space, onClick }: { space: Space; onClick: () => void }) {
  const [hovBar, setHovBar] = useState(false);
  return (
    <div style={{ background: '#111', border: '1px solid #222', borderRadius: 0, overflow: 'hidden', cursor: 'pointer' }} onClick={onClick}>
      {/* Photo 16:9 */}
      <div style={{ position: 'relative', paddingTop: '56.25%', background: '#0a0a0a', overflow: 'hidden' }}>
        {space.image_url
          ? <img src={space.image_url} alt={space.name} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
          : <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, opacity: 0.1, color: '#888' }}>{catIcon(space.category)}</div>
        }
        {space.capacity && (
          <div style={{ position: 'absolute', top: 5, right: 5, background: 'rgba(0,0,0,0.75)', border: '1px solid #333', padding: '2px 6px', fontSize: 9, fontWeight: 700, color: '#a0a0a0' }}>
            {space.capacity} pax
          </div>
        )}
      </div>
      {/* Info */}
      <div style={{ padding: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#e5e5e5', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{space.name}</div>
        {space.location && <div style={{ fontSize: 9, color: '#555', marginBottom: 2 }}>{space.location}</div>}
        {(space.amenities || []).length > 0 && (
          <div style={{ fontSize: 9, color: '#444', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(space.amenities || []).join(' · ')}</div>
        )}
        <div style={{ fontSize: 10, fontWeight: 700, color: '#60a5fa', marginTop: 4 }}>
          {space.hourly_rate ? `$${space.hourly_rate}/hr` : space.daily_rate ? `$${space.daily_rate}/day` : 'Free'}
        </div>
      </div>
      {/* Bottom bar */}
      <div
        onMouseEnter={() => setHovBar(true)} onMouseLeave={() => setHovBar(false)}
        style={{ height: 28, background: hovBar ? '#1e3a5f' : '#0a0a0a', borderTop: '1px solid #222', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.15s' }}>
        <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: '#3b82f6', letterSpacing: '0.06em' }}>BOOK NOW</span>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// PAGE 2 — VENUE DETAIL
// ═════════════════════════════════════════════════════════════════════════════
function VenueDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();
  const [tab, setTab] = useState<'availability' | 'details' | 'photos'>('availability');
  const [checkDate, setCheckDate] = useState(todayStr());
  const [slots, setSlots] = useState<TimeSlot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  useEffect(() => {
    fetch(`${API}/public/bookings/spaces`)
      .then(r => r.json())
      .then(d => { setSpaces(d.spaces || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const space = spaces.find(s => s.id === id);

  const checkAvailability = useCallback(async (date: string) => {
    if (!id) return;
    setSlotsLoading(true);
    try {
      const r = await fetch(`${API}/public/bookings/availability`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ space_id: id, date }),
      });
      const d = await r.json();
      setSlots(d.slots || []);
    } catch { setSlots([]); }
    setSlotsLoading(false);
  }, [id]);

  useEffect(() => { if (id) checkAvailability(checkDate); }, [checkDate, id, checkAvailability]);

  if (loading) return <div style={{ minHeight: '100vh', background: '#0a0a0a' }}><PortalHeader /><Spinner /></div>;
  if (!space) return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a' }}>
      <PortalHeader />
      <div style={{ maxWidth: 600, margin: '64px auto', textAlign: 'center', padding: '0 24px' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
        <h2 style={{ color: '#e5e5e5' }}>Venue not found</h2>
        <button onClick={() => nav('/book')} style={{ marginTop: 16, padding: '10px 24px', background: '#1a56db', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>← Back to Browse</button>
      </div>
      <PortalFooter />
    </div>
  );

  const availableSlots = slots.filter(s => s.available);

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', flexDirection: 'column' }}>
      <PortalHeader />

      {/* Breadcrumb */}
      <div style={{ background: '#0d0d0d', borderBottom: '1px solid #1a1a1a', padding: '8px 24px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', fontSize: 10, color: '#555' }}>
          <span style={{ cursor: 'pointer', color: '#3b82f6' }} onClick={() => nav('/book')}>Browse Venues</span>
          <span style={{ margin: '0 8px' }}>›</span>
          <span style={{ color: '#a0a0a0', fontWeight: 600 }}>{space.name}</span>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 24px', width: '100%', flex: 1, display: 'grid', gridTemplateColumns: '1fr 340px', gap: 28, alignItems: 'start' }}>
        {/* Left column */}
        <div>
          {/* Hero image */}
          <div style={{ position: 'relative', paddingTop: '56.25%', background: '#0a0a0a', marginBottom: 20, overflow: 'hidden' }}>
            {!space.image_url && <span style={{ fontSize: 80, opacity: 0.35 }}>{catIcon(space.category)}</span>}
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h1 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: '#e5e5e5' }}>{space.name}</h1>
              {space.location && <div style={{ color: '#555', fontSize: 11 }}>📍 {space.location}</div>}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {space.category && <Badge label={space.category} color={catColor(space.category)} />}
              {space.capacity && <Badge label={`👥 Up to ${space.capacity}`} color="#555555" />}
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', marginBottom: 24, gap: 0 }}>
            {(['availability', 'details', 'photos'] as const).map(t => (
              <button key={t} onClick={() => setTab(t)}
                style={{ padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: tab === t ? '#1a56db' : '#555555', borderBottom: `2px solid ${tab === t ? '#1a56db' : 'transparent'}`, marginBottom: -2, textTransform: 'capitalize', transition: 'color 0.15s' }}>
                {t === 'availability' ? '📅 Availability' : t === 'details' ? 'ℹ Details' : '🖼 Photos'}
              </button>
            ))}
          </div>

          {tab === 'availability' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <label style={{ fontSize: 14, color: '#555555', fontWeight: 500 }}>Check date:</label>
                <input type="date" value={checkDate} min={todayStr()} onChange={e => setCheckDate(e.target.value)}
                  style={{ padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, outline: 'none', color: '#e5e5e5' }} />
              </div>
              {slotsLoading ? <Spinner /> : (
                <div>
                  {slots.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '32px 0', color: '#a0a0a0', fontSize: 14 }}>No time slots available for this date</div>
                  ) : (
                    <>
                      <div style={{ fontSize: 10, color: '#555', marginBottom: 12 }}>{availableSlots.length} slot{availableSlots.length !== 1 ? 's' : ''} available</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
                        {slots.map((slot, i) => (
                          <div key={i} style={{ padding: '10px 8px', textAlign: 'center', borderRadius: 8, border: `1.5px solid ${slot.available ? '#bbf7d0' : '#e2e8f0'}`, background: slot.available ? '#f0fdf4' : '#f8fafc', color: slot.available ? '#16a34a' : '#94a3b8', fontSize: 12, fontWeight: 600 }}>
                            {slot.start}–{slot.end}
                            <div style={{ fontSize: 10, marginTop: 2, fontWeight: 400 }}>{slot.available ? '✓ Available' : '✗ Booked'}</div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {tab === 'details' && (
            <div>
              {space.description && <p style={{ color: '#555555', lineHeight: 1.7, marginBottom: 20 }}>{space.description}</p>}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                {space.hourly_rate && <DetailItem icon="💰" label="Hourly Rate" value={`$${space.hourly_rate}`} />}
                {space.daily_rate && <DetailItem icon="📆" label="Daily Rate" value={`$${space.daily_rate}`} />}
                {space.capacity && <DetailItem icon="👥" label="Capacity" value={`${space.capacity} people`} />}
                {space.min_duration_hours && <DetailItem icon="⏱" label="Min Duration" value={`${space.min_duration_hours}h`} />}
                {space.max_duration_hours && <DetailItem icon="⏳" label="Max Duration" value={`${space.max_duration_hours}h`} />}
                {space.booking_lead_days && <DetailItem icon="📅" label="Advance Booking" value={`${space.booking_lead_days} days`} />}
              </div>
              {space.amenities && space.amenities.length > 0 && (
                <div style={{ marginTop: 20 }}>
                  <div style={{ fontWeight: 700, color: '#e5e5e5', marginBottom: 10 }}>Amenities</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {space.amenities.map((a, i) => <span key={i} style={{ background: '#eff6ff', color: '#1a56db', border: '1px solid #bfdbfe', borderRadius: 6, padding: '4px 12px', fontSize: 13 }}>✓ {a}</span>)}
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === 'photos' && (
            <div style={{ textAlign: 'center', padding: '48px 0', color: '#a0a0a0' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🖼</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#555555' }}>No photos uploaded yet</div>
              <div style={{ fontSize: 13, marginTop: 8 }}>Contact the facility manager to request photos</div>
            </div>
          )}
        </div>

        {/* Right: sticky booking card */}
        <div style={{ position: 'sticky', top: 84 }}>
          <Card style={{ padding: 24 }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#0a0a0a', marginBottom: 4 }}>
              {space.hourly_rate ? `$${space.hourly_rate}/hr` : space.daily_rate ? `$${space.daily_rate}/day` : 'Free'}
            </div>
            {space.hourly_rate && <div style={{ fontSize: 10, color: '#555', marginBottom: 20 }}>Plus applicable fees and taxes</div>}
            <button onClick={() => nav('/book/new', { state: { space } })}
              style={{ width: '100%', padding: '13px', background: '#1a56db', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 15, fontWeight: 700, marginBottom: 12 }}>
              Book this venue
            </button>
            <button onClick={() => nav('/book')}
              style={{ width: '100%', padding: '11px', background: '#f1f5f9', color: '#555555', border: '1px solid #e2e8f0', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
              ← Browse other venues
            </button>
            {(space.capacity || space.min_duration_hours) && (
              <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #f1f5f9' }}>
                {space.capacity && <div style={{ fontSize: 10, color: '#555', marginBottom: 6 }}>👥 Capacity: {space.capacity} people</div>}
                {space.min_duration_hours && <div style={{ fontSize: 10, color: '#555', marginBottom: 6 }}>⏱ Min booking: {space.min_duration_hours}h</div>}
                {space.booking_lead_days && <div style={{ fontSize: 10, color: '#555' }}>📅 Book up to {space.booking_lead_days} days ahead</div>}
              </div>
            )}
          </Card>
        </div>
      </div>
      <PortalFooter />
    </div>
  );
}

function DetailItem({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 14px', display: 'flex', gap: 10, alignItems: 'center' }}>
      <span style={{ fontSize: 20 }}>{icon}</span>
      <div>
        <div style={{ fontSize: 11, color: '#a0a0a0', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#e5e5e5' }}>{value}</div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// PAGE 3 — 4-STEP BOOKING FLOW
// ═════════════════════════════════════════════════════════════════════════════
type BookingStep = 1 | 2 | 3 | 4 | 5;

interface BookingState {
  space: Space | null;
  date: string;
  startTime: string;
  endTime: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  notes: string;
  termsAccepted: boolean;
  result: BookingResult | null;
}

interface BookingResult {
  id: string;
  reference: string;
  space_name?: string;
  spaceName?: string;
  booking_date?: string;
  bookingDate?: string;
  start_time?: string;
  startTime?: string;
  end_time?: string;
  endTime?: string;
  customer_name?: string;
  firstName?: string;
  lastName?: string;
  customer_email?: string;
  email?: string;
  status: string;
  pin_code?: string;
  pinCode?: string;
  total_cost?: number;
  message?: string;
}

function NewBookingPage() {
  const nav = useNavigate();
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [step, setStep] = useState<BookingStep>(1);
  const [state, setState] = useState<BookingState>({
    space: null, date: '', startTime: '', endTime: '',
    firstName: '', lastName: '', email: '', phone: '', notes: '',
    termsAccepted: false, result: null,
  });
  const [slots, setSlots] = useState<TimeSlot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const location = useLocation();
  // Try to get space from nav state (coming from venue detail)
  useEffect(() => {
    const locState = location.state as { space?: Space } | null;
    if (locState?.space) {
      setState(p => ({ ...p, space: locState.space! }));
    }
    // Always load spaces list
    fetch(`${API}/public/bookings/spaces`)
      .then(r => r.json())
      .then(d => setSpaces(d.spaces || []))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSlots = useCallback(async (spaceId: string, date: string) => {
    setSlotsLoading(true);
    try {
      const r = await fetch(`${API}/public/bookings/availability`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ space_id: spaceId, date }),
      });
      const d = await r.json();
      setSlots(d.slots || []);
    } catch { setSlots([]); }
    setSlotsLoading(false);
  }, []);

  useEffect(() => {
    if (state.space && state.date) fetchSlots(state.space.id, state.date);
  }, [state.space, state.date, fetchSlots]);

  const up = (fields: Partial<BookingState>) => setState(p => ({ ...p, ...fields }));

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const r = await fetch(`${API}/public/bookings/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          space_id: state.space!.id,
          booking_date: state.date,
          start_time: state.startTime,
          end_time: state.endTime,
          firstName: state.firstName,
          lastName: state.lastName,
          email: state.email,
          phone: state.phone,
          notes: state.notes,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || d.error || 'Booking failed');
      up({ result: d });
      setStep(5);
    } catch (e: any) {
      setError(e.message);
    }
    setSubmitting(false);
  };

  const availableSlots = slots.filter(s => s.available);

  const STEPS = ['Date & Time', 'Your Details', 'Review', 'Confirm'];

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', flexDirection: 'column' }}>
      <PortalHeader />

      {step < 5 && (
        <>
          {/* Progress */}
          <div style={{ background: '#0d0d0d', borderBottom: '1px solid #1a1a1a', padding: '12px 24px' }}>
            <div style={{ maxWidth: 680, margin: '0 auto' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                {STEPS.map((label, i) => {
                  const n = i + 1;
                  const done = step > n;
                  const active = step === n;
                  return (
                    <React.Fragment key={n}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                        <div style={{ width: 18, height: 18, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, background: done ? '#16a34a' : active ? '#3b82f6' : '#1a1a1a', color: (done || active) ? '#fff' : '#444', marginBottom: 4, border: `1px solid ${done ? '#16a34a' : active ? '#3b82f6' : '#333'}` }}>
                          {done ? '✓' : n}
                        </div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: active ? '#60a5fa' : done ? '#16a34a' : '#333', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
                      </div>
                      {i < STEPS.length - 1 && <div style={{ height: 1, flex: 0.5, background: step > n ? '#16a34a' : '#222', margin: '0 4px', marginBottom: 12 }} />}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '32px 24px', width: '100%', flex: 1 }}>
        {step < 5 && state.space && (
          <div style={{ background: '#0c1e3d', border: '1px solid #1e3a5f', borderRadius: 0, padding: '8px 12px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20 }}>{catIcon(state.space.category)}</span>
            <div>
              <div style={{ fontWeight: 700, color: '#e5e5e5', fontSize: 15 }}>{state.space.name}</div>
              {state.space.location && <div style={{ fontSize: 10, color: '#555' }}>📍 {state.space.location}</div>}
            </div>
            <button onClick={() => up({ space: null })} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#a0a0a0', cursor: 'pointer', fontSize: 18 }}>✕</button>
          </div>
        )}

        {/* STEP 1 — Date & Time */}
        {step === 1 && (
          <Card style={{ padding: 28 }}>
            <h2 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 700, color: '#e5e5e5' }}>Select Date & Time</h2>

            {/* Space picker if not pre-selected */}
            {!state.space && (
              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', fontWeight: 700, color: '#888', marginBottom: 6, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Select a venue</label>
                <select value={state.space ? (state.space as Space).id : ''} onChange={e => { const s = spaces.find(x => x.id === e.target.value); up({ space: s || null }); }}
                  style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', color: '#e5e5e5' }}>
                  <option value="">Choose a venue…</option>
                  {spaces.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            )}

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontWeight: 700, color: '#888', marginBottom: 6, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Booking date</label>
              <input type="date" value={state.date} min={todayStr()} onChange={e => up({ date: e.target.value, startTime: '', endTime: '' })}
                style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, outline: 'none', color: '#e5e5e5', boxSizing: 'border-box' }} />
            </div>

            {state.space && state.date && (
              <div>
                {slotsLoading ? <Spinner /> : availableSlots.length === 0 ? (
                  <Alert type="info">No available slots on this date. Please try another day.</Alert>
                ) : (
                  <>
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', fontWeight: 700, color: '#888', marginBottom: 6, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Start time</label>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                        {availableSlots.map((slot, i) => (
                          <button key={i} onClick={() => up({ startTime: slot.start, endTime: '' })}
                            style={{ padding: '5px 4px', textAlign: 'center', borderRadius: 0, border: `1px solid ${state.startTime === slot.start ? '#3b82f6' : '#333'}`, background: state.startTime === slot.start ? '#1e3a5f' : '#1a1a1a', color: state.startTime === slot.start ? '#60a5fa' : '#555', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                            {slot.start}
                          </button>
                        ))}
                      </div>
                    </div>

                    {state.startTime && (
                      <div>
                        <label style={{ display: 'block', fontWeight: 700, color: '#888', marginBottom: 6, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>End time</label>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                          {availableSlots.filter(s => s.start > state.startTime).map((slot, i) => (
                            <button key={i} onClick={() => up({ endTime: slot.end })}
                              style={{ padding: '5px 4px', textAlign: 'center', borderRadius: 0, border: `1px solid ${state.endTime === slot.end ? '#3b82f6' : '#333'}`, background: state.endTime === slot.end ? '#1e3a5f' : '#1a1a1a', color: state.endTime === slot.end ? '#60a5fa' : '#555', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                              {slot.end}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div style={{ marginTop: 28, display: 'flex', justifyContent: 'space-between' }}>
              <button onClick={() => nav('/book')} style={{ padding: '6px 14px', background: '#1a1a1a', color: '#888', border: '1px solid #333', borderRadius: 0, cursor: 'pointer', fontSize: 10, fontWeight: 700 }}>← Browse</button>
              <button onClick={() => setStep(2)} disabled={!state.space || !state.date || !state.startTime || !state.endTime}
                style={{ padding: '6px 16px', background: !state.space || !state.date || !state.startTime || !state.endTime ? '#1a1a1a' : '#16a34a', color: !state.space || !state.date || !state.startTime || !state.endTime ? '#444' : '#fff', border: 'none', borderRadius: 0, cursor: !state.space || !state.date || !state.startTime || !state.endTime ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 700 }}>
                Next →
              </button>
            </div>
          </Card>
        )}

        {/* STEP 2 — Details */}
        {step === 2 && (
          <Card style={{ padding: 28 }}>
            <h2 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 700, color: '#e5e5e5' }}>Your Details</h2>
            <p style={{ margin: '0 0 24px', color: '#555', fontSize: 11 }}>Please provide your contact information for this booking</p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <FormField label="First name *" value={state.firstName} onChange={v => up({ firstName: v })} placeholder="Jane" />
              <FormField label="Last name *" value={state.lastName} onChange={v => up({ lastName: v })} placeholder="Smith" />
            </div>
            <div style={{ marginBottom: 16 }}>
              <FormField label="Email address *" value={state.email} onChange={v => up({ email: v })} placeholder="jane@example.com" type="email" />
            </div>
            <div style={{ marginBottom: 16 }}>
              <FormField label="Phone number" value={state.phone} onChange={v => up({ phone: v })} placeholder="0400 000 000" type="tel" />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600, color: '#374151', marginBottom: 6, fontSize: 14 }}>Additional notes</label>
              <textarea value={state.notes} onChange={e => up({ notes: e.target.value })} placeholder="Any special requirements or notes…" rows={3}
                style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, outline: 'none', resize: 'vertical', fontFamily: 'inherit', color: '#e5e5e5', boxSizing: 'border-box' }} />
            </div>

            <div style={{ marginTop: 28, display: 'flex', justifyContent: 'space-between' }}>
              <button onClick={() => setStep(1)} style={{ padding: '6px 14px', background: '#1a1a1a', color: '#888', border: '1px solid #333', borderRadius: 0, cursor: 'pointer', fontSize: 10, fontWeight: 700 }}>← Back</button>
              <button onClick={() => setStep(3)} disabled={!state.firstName || !state.lastName || !state.email}
                style={{ padding: '6px 16px', background: !state.firstName || !state.lastName || !state.email ? '#1a1a1a' : '#16a34a', color: !state.firstName || !state.lastName || !state.email ? '#444' : '#fff', border: 'none', borderRadius: 0, cursor: !state.firstName || !state.lastName || !state.email ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 700 }}>
                Next →
              </button>
            </div>
          </Card>
        )}

        {/* STEP 3 — Review + Terms */}
        {step === 3 && (
          <Card style={{ padding: 28 }}>
            <h2 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 700, color: '#e5e5e5' }}>Review & Confirm</h2>

            <div style={{ background: '#0d0d0d', border: '1px solid #1a1a1a', borderRadius: 0, padding: 14, marginBottom: 16 }}>
              <div style={{ fontWeight: 700, color: '#e5e5e5', fontSize: 16, marginBottom: 16 }}>Booking Summary</div>
              <SummaryRow label="Venue" value={state.space?.name || ''} />
              {state.space?.location && <SummaryRow label="Location" value={state.space.location} />}
              <SummaryRow label="Date" value={formatDate(state.date)} />
              <SummaryRow label="Time" value={`${state.startTime} – ${state.endTime}`} />
              <SummaryRow label="Name" value={`${state.firstName} ${state.lastName}`} />
              <SummaryRow label="Email" value={state.email} />
              {state.phone && <SummaryRow label="Phone" value={state.phone} />}
              {state.space?.hourly_rate && (
                <>
                  <div style={{ borderTop: '1px solid #e2e8f0', margin: '12px 0' }} />
                  <SummaryRow label="Estimated cost" value={`$${calcCost(state.startTime, state.endTime, state.space.hourly_rate)}`} bold />
                </>
              )}
            </div>

            <div style={{ background: '#2d1f00', border: '1px solid #92400e', borderRadius: 0, padding: 12, marginBottom: 16 }}>
              <div style={{ fontWeight: 700, color: '#f59e0b', marginBottom: 6, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Terms & Conditions</div>
              <div style={{ fontSize: 10, color: '#a0a0a0', lineHeight: 1.6 }}>
                By making this booking you agree to comply with the facility's rules and regulations, provide accurate contact information, give 48 hours notice for cancellations, and accept liability for any damage caused during your hire. A PIN code will be issued upon approval for access.
              </div>
            </div>

            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', marginBottom: 24 }}>
              <input type="checkbox" checked={state.termsAccepted} onChange={e => up({ termsAccepted: e.target.checked })}
                style={{ width: 16, height: 16, marginTop: 2, accentColor: '#1a56db', flexShrink: 0 }} />
              <span style={{ fontSize: 11, color: '#a0a0a0', lineHeight: 1.5 }}>I accept the terms and conditions and confirm the booking details are correct</span>
            </label>

            {error && <Alert type="error">{error}</Alert>}

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button onClick={() => setStep(2)} style={{ padding: '6px 14px', background: '#1a1a1a', color: '#888', border: '1px solid #333', borderRadius: 0, cursor: 'pointer', fontSize: 10, fontWeight: 700 }}>← Back</button>
              <button onClick={submit} disabled={!state.termsAccepted || submitting}
                style={{ height: 28, padding: '0 20px', background: !state.termsAccepted || submitting ? '#1a1a1a' : '#16a34a', color: !state.termsAccepted || submitting ? '#444' : '#fff', border: 'none', borderRadius: 0, cursor: !state.termsAccepted || submitting ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {submitting ? <><div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid #fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /> Submitting…</> : '✓ Confirm Booking'}
              </button>
            </div>
          </Card>
        )}

        {/* STEP 5 — Confirmation */}
        {step === 5 && state.result && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 64, marginBottom: 20 }}>🎉</div>
            <Card style={{ padding: 36, maxWidth: 520, margin: '0 auto' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>✅</div>
              <h2 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 700, color: '#16a34a' }}>Booking Confirmed!</h2>
              <p style={{ color: '#555', margin: '0 0 16px', fontSize: 11, lineHeight: 1.6 }}>Your booking has been received. You'll get an email confirmation shortly.</p>

              <div style={{ background: '#052e16', border: '1px solid #14532d', borderRadius: 0, padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 10, color: '#555', fontWeight: 600, marginBottom: 6 }}>BOOKING REFERENCE</div>
                <div style={{ fontSize: 28, fontWeight: 900, color: '#16a34a', letterSpacing: '0.15em', fontFamily: 'monospace' }}>{state.result.reference}</div>
              </div>

              <div style={{ textAlign: 'left', marginBottom: 24 }}>
                <SummaryRow label="Venue" value={state.result.space_name || state.result.spaceName || state.space?.name || ''} />
                <SummaryRow label="Date" value={formatDate(state.result.booking_date || state.result.bookingDate || state.date)} />
                <SummaryRow label="Time" value={`${state.result.start_time || state.result.startTime || state.startTime} – ${state.result.end_time || state.result.endTime || state.endTime}`} />
                <SummaryRow label="Status" value={state.result.status} />
                {(state.result.pin_code || state.result.pinCode) && (
                  <SummaryRow label="Access PIN" value={state.result.pin_code || state.result.pinCode || ''} bold />
                )}
              </div>

              {state.result.message && <Alert type="info">{state.result.message}</Alert>}

              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button onClick={() => nav('/book/lookup')} style={{ padding: '6px 14px', background: '#1e3a5f', color: '#60a5fa', border: '1px solid #1e3a5f', borderRadius: 0, cursor: 'pointer', fontSize: 10, fontWeight: 700 }}>View booking status</button>
                <button onClick={() => nav('/book')} style={{ padding: '6px 14px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 0, cursor: 'pointer', fontSize: 10, fontWeight: 700 }}>Book another venue</button>
              </div>
            </Card>
          </div>
        )}
      </div>
      <PortalFooter />
    </div>
  );
}

function FormField({ label, value, onChange, placeholder, type = 'text' }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string }) {
  return (
    <div>
      <label style={{ display: 'block', fontWeight: 700, color: '#888', marginBottom: 4, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: '100%', padding: '7px 10px', border: '1px solid #333', borderRadius: 0, fontSize: 12, outline: 'none', color: '#e5e5e5', background: '#1a1a1a', boxSizing: 'border-box', fontFamily: 'inherit' }}
        onFocus={e => e.target.style.borderColor = '#3b82f6'}
        onBlur={e => e.target.style.borderColor = '#333'}
      />
    </div>
  );
}

function SummaryRow({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #1a1a1a', gap: 12 }}>
      <span style={{ fontSize: 11, color: '#555', fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: 11, color: bold ? '#e5e5e5' : '#a0a0a0', fontWeight: bold ? 700 : 400, textAlign: 'right' }}>{value}</span>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// PAGE 4 — LOGIN
// ═════════════════════════════════════════════════════════════════════════════
function LoginPage() {
  const { login, user } = useContext(PubAuth);
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (user) nav('/book/my-bookings'); }, [user, nav]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(email, password);
      nav('/book/my-bookings');
    } catch (err: any) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', flexDirection: 'column' }}>
      <PortalHeader />
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
        <div style={{ width: '100%', maxWidth: 420 }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ width: 40, height: 40, background: '#1a1a1a', border: '1px solid #333', borderRadius: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, margin: '0 auto 12px' }}>🔐</div>
            <h1 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 700, color: '#e5e5e5' }}>Sign In</h1>
            <p style={{ margin: 0, color: '#555', fontSize: 11 }}>Access your bookings and account</p>
          </div>
          <Card style={{ padding: 32 }}>
            {error && <Alert type="error">{error}</Alert>}
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 16 }}>
                <FormField label="Email address" value={email} onChange={setEmail} placeholder="you@example.com" type="email" />
              </div>
              <div style={{ marginBottom: 24 }}>
                <FormField label="Password" value={password} onChange={setPassword} placeholder="••••••••" type="password" />
              </div>
              <button type="submit" disabled={loading || !email || !password}
                style={{ width: '100%', padding: '6px', height: 32, background: loading || !email || !password ? '#1a1a1a' : '#1e3a5f', color: loading || !email || !password ? '#444' : '#60a5fa', border: `1px solid ${loading || !email || !password ? '#333' : '#1e3a5f'}`, borderRadius: 0, cursor: loading || !email || !password ? 'not-allowed' : 'pointer', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                {loading ? <><div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid #fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /> Signing in…</> : 'Sign In'}
              </button>
            </form>
            <div style={{ marginTop: 20, textAlign: 'center', fontSize: 10, color: '#555' }}>
              Don't have an account?{' '}
              <span style={{ color: '#3b82f6', fontWeight: 600, cursor: 'pointer' }} onClick={() => nav('/book/register')}>Register here</span>
            </div>
          </Card>
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <span style={{ color: '#444', fontSize: 10, cursor: 'pointer' }} onClick={() => nav('/book/lookup')}>
              Have a booking reference? Look it up →
            </span>
          </div>
        </div>
      </div>
      <PortalFooter />
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// PAGE 5 — REGISTER
// ═════════════════════════════════════════════════════════════════════════════
function RegisterPage() {
  const nav = useNavigate();
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const up = (k: keyof typeof form) => (v: string) => setForm(p => ({ ...p, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.password !== form.confirm) { setError('Passwords do not match'); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API}/auth/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: `${form.firstName} ${form.lastName}`, email: form.email, password: form.password }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || d.error || 'Registration failed');
      setDone(true);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  };

  if (done) return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', flexDirection: 'column' }}>
      <PortalHeader />
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <Card style={{ padding: 40, maxWidth: 420, width: '100%', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
          <h2 style={{ margin: '0 0 12px', color: '#16a34a', fontSize: 22, fontWeight: 800 }}>Account Created!</h2>
          <p style={{ color: '#555', fontSize: 11, marginBottom: 24 }}>Your account has been created. You can now sign in to manage your bookings.</p>
          <button onClick={() => nav('/book/login')} style={{ padding: '5px 16px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 0, cursor: 'pointer', fontWeight: 700, fontSize: 10 }}>Sign In</button>
        </Card>
      </div>
      <PortalFooter />
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', flexDirection: 'column' }}>
      <PortalHeader />
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
        <div style={{ width: '100%', maxWidth: 460 }}>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{ width: 40, height: 40, background: '#1a1a1a', border: '1px solid #333', borderRadius: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, margin: '0 auto 12px' }}>👤</div>
            <h1 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 700, color: '#e5e5e5' }}>Create Account</h1>
            <p style={{ margin: 0, color: '#555', fontSize: 11 }}>Register to manage your bookings online</p>
          </div>
          <Card style={{ padding: 32 }}>
            {error && <Alert type="error">{error}</Alert>}
            <form onSubmit={submit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <FormField label="First name *" value={form.firstName} onChange={up('firstName')} placeholder="Jane" />
                <FormField label="Last name *" value={form.lastName} onChange={up('lastName')} placeholder="Smith" />
              </div>
              <div style={{ marginBottom: 14 }}><FormField label="Email *" value={form.email} onChange={up('email')} placeholder="you@example.com" type="email" /></div>
              <div style={{ marginBottom: 14 }}><FormField label="Password *" value={form.password} onChange={up('password')} placeholder="••••••••" type="password" /></div>
              <div style={{ marginBottom: 24 }}><FormField label="Confirm password *" value={form.confirm} onChange={up('confirm')} placeholder="••••••••" type="password" /></div>
              <button type="submit" disabled={loading || !form.firstName || !form.email || !form.password}
                style={{ width: '100%', padding: 13, background: '#1a56db', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 15, fontWeight: 700 }}>
                {loading ? 'Creating account…' : 'Create Account'}
              </button>
            </form>
            <div style={{ marginTop: 18, textAlign: 'center', fontSize: 10, color: '#555' }}>
              Already have an account?{' '}
              <span style={{ color: '#3b82f6', fontWeight: 600, cursor: 'pointer' }} onClick={() => nav('/book/login')}>Sign in</span>
            </div>
          </Card>
        </div>
      </div>
      <PortalFooter />
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// PAGE 6 — MY BOOKINGS
// ═════════════════════════════════════════════════════════════════════════════
interface MyBooking {
  id: string;
  reference: string;
  space_name?: string;
  spaceName?: string;
  booking_date?: string;
  bookingDate?: string;
  start_time?: string;
  startTime?: string;
  end_time?: string;
  endTime?: string;
  status: string;
  total_cost?: number;
  pin_code?: string;
  pinCode?: string;
}

function MyBookingsPage() {
  const { user, logout } = useContext(PubAuth);
  const nav = useNavigate();
  const [bookings, setBookings] = useState<MyBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<'upcoming' | 'past' | 'cancelled'>('upcoming');
  const [pinVisible, setPinVisible] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!user) { nav('/book/login'); return; }
    const token = localStorage.getItem('lemai_pub_tok');
    fetch(`${API}/bookings/`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { setBookings(d.bookings || []); setLoading(false); })
      .catch(() => { setError('Unable to load bookings'); setLoading(false); });
  }, [user, nav]);

  const today = todayStr();
  const filtered = bookings.filter(b => {
    const date = b.booking_date || b.bookingDate || '';
    if (tab === 'cancelled') return b.status === 'cancelled';
    if (tab === 'past') return b.status !== 'cancelled' && date < today;
    return b.status !== 'cancelled' && date >= today;
  });

  const tabCount = (t: 'upcoming' | 'past' | 'cancelled') => bookings.filter(b => {
    const date = b.booking_date || b.bookingDate || '';
    if (t === 'cancelled') return b.status === 'cancelled';
    if (t === 'past') return b.status !== 'cancelled' && date < today;
    return b.status !== 'cancelled' && date >= today;
  }).length;

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', flexDirection: 'column' }}>
      <PortalHeader />
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '32px 24px', width: '100%', flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ margin: '0 0 4px', fontSize: 26, fontWeight: 800, color: '#0a0a0a' }}>My Bookings</h1>
            {user && <div style={{ fontSize: 14, color: '#555555' }}>Signed in as {user.email}</div>}
          </div>
          <button onClick={() => nav('/book/new')} style={{ padding: '6px 14px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 0, cursor: 'pointer', fontSize: 10, fontWeight: 700 }}>+ New Booking</button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', marginBottom: 24 }}>
          {(['upcoming', 'past', 'cancelled'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)}
              style={{ padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: tab === t ? '#1a56db' : '#555555', borderBottom: `2px solid ${tab === t ? '#1a56db' : 'transparent'}`, marginBottom: -2, textTransform: 'capitalize' }}>
              {t} ({tabCount(t)})
            </button>
          ))}
        </div>

        {loading && <Spinner />}
        {error && <Alert type="error">{error}</Alert>}

        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 0', color: '#a0a0a0' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📅</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#555555' }}>No {tab} bookings</div>
            {tab === 'upcoming' && (
              <button onClick={() => nav('/book/new')} style={{ marginTop: 20, padding: '10px 24px', background: '#1a56db', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>Book a venue</button>
            )}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {filtered.map(b => {
            const date = b.booking_date || b.bookingDate || '';
            const start = b.start_time || b.startTime || '';
            const end = b.end_time || b.endTime || '';
            const name = b.space_name || b.spaceName || 'Venue';
            const pin = b.pin_code || b.pinCode;
            const statusColors: Record<string, string> = {
              confirmed: '#16a34a', pending: '#d97706', approved: '#16a34a',
              cancelled: '#dc2626', rejected: '#dc2626', completed: '#6366f1',
            };
            const sc = statusColors[b.status] || '#555555';
            return (
              <Card key={b.id} style={{ padding: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, color: '#0a0a0a', fontSize: 16, marginBottom: 4 }}>{name}</div>
                    <div style={{ fontSize: 13, color: '#555555', marginBottom: 2 }}>📅 {formatDate(date)} · {start} – {end}</div>
                    <div style={{ fontSize: 13, color: '#a0a0a0', fontFamily: 'monospace' }}>Ref: {b.reference}</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                    <span style={{ background: `${sc}18`, color: sc, border: `1px solid ${sc}40`, borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 700, textTransform: 'capitalize' }}>{b.status}</span>
                    {b.total_cost && <span style={{ fontSize: 13, fontWeight: 700, color: '#e5e5e5' }}>${b.total_cost}</span>}
                  </div>
                </div>
                {pin && (
                  <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 10, color: '#555', fontWeight: 500 }}>🔑 Access PIN:</span>
                    <span style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 700, color: '#e5e5e5', letterSpacing: '0.1em' }}>
                      {pinVisible[b.id] ? pin : '••••••'}
                    </span>
                    <button onClick={() => setPinVisible(p => ({ ...p, [b.id]: !p[b.id] }))}
                      style={{ fontSize: 12, color: '#1a56db', background: '#eff6ff', border: 'none', cursor: 'pointer', fontWeight: 600, padding: '2px 8px', borderRadius: 4 } as React.CSSProperties}>
                      {pinVisible[b.id] ? 'Hide' : 'Show PIN'}
                    </button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </div>
      <PortalFooter />
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// PAGE 7 — LOOKUP
// ═════════════════════════════════════════════════════════════════════════════
function LookupPage() {
  const nav = useNavigate();
  const [ref, setRef] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');
  const [pinVisible, setPinVisible] = useState(false);

  const lookup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ref.trim()) return;
    setLoading(true); setError(''); setResult(null);
    try {
      const r = await fetch(`${API}/public/bookings/${ref.trim().toUpperCase()}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || d.error || 'Booking not found');
      setResult(d);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', flexDirection: 'column' }}>
      <PortalHeader />
      <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '48px 24px' }}>
        <div style={{ width: '100%', maxWidth: 520 }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ width: 40, height: 40, background: '#1a1a1a', border: '1px solid #333', borderRadius: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, margin: '0 auto 12px' }}>🔎</div>
            <h1 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 700, color: '#e5e5e5' }}>Look Up Booking</h1>
            <p style={{ margin: 0, color: '#555', fontSize: 11 }}>Enter your booking reference to check status, time, and access PIN</p>
          </div>

          <Card style={{ padding: 32, marginBottom: 24 }}>
            <form onSubmit={lookup}>
              <label style={{ display: 'block', fontWeight: 700, color: '#888', marginBottom: 6, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Booking Reference</label>
              <div style={{ display: 'flex', gap: 10 }}>
                <input value={ref} onChange={e => setRef(e.target.value.toUpperCase())} placeholder="e.g. BK-XXXXXX"
                  style={{ flex: 1, padding: '11px 14px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 15, fontFamily: 'monospace', fontWeight: 600, outline: 'none', color: '#e5e5e5', letterSpacing: '0.05em' }}
                  onFocus={e => e.target.style.borderColor = '#1a56db'}
                  onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                />
                <button type="submit" disabled={loading || !ref.trim()}
                  style={{ padding: '11px 20px', background: '#1a56db', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {loading ? '…' : 'Look Up'}
                </button>
              </div>
            </form>
          </Card>

          {error && <Alert type="error">{error}</Alert>}

          {result && (
            <Card style={{ padding: 28 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div style={{ fontWeight: 700, color: '#0a0a0a', fontSize: 18 }}>Booking Found</div>
                <StatusBadge status={result.status} />
              </div>
              <SummaryRow label="Reference" value={result.reference || ref} />
              <SummaryRow label="Venue" value={result.space_name || result.spaceName || '—'} />
              <SummaryRow label="Date" value={formatDate(result.booking_date || result.bookingDate || '')} />
              <SummaryRow label="Time" value={`${result.start_time || result.startTime || ''} – ${result.end_time || result.endTime || ''}`} />
              <SummaryRow label="Booked by" value={result.customer_name || (result.firstName ? `${result.firstName} ${result.lastName}` : '—')} />
              {result.total_cost && <SummaryRow label="Total cost" value={`$${result.total_cost}`} bold />}
              {(result.pin_code || result.pinCode) && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f1f5f9' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontSize: 12, color: '#a0a0a0', fontWeight: 600, marginBottom: 4 }}>ACCESS PIN</div>
                      <div style={{ fontSize: 24, fontWeight: 900, fontFamily: 'monospace', letterSpacing: '0.15em', color: pinVisible ? '#1a56db' : '#111111' }}>
                        {pinVisible ? (result.pin_code || result.pinCode) : '••••••'}
                      </div>
                    </div>
                    <button onClick={() => setPinVisible(p => !p)}
                      style={{ padding: '8px 16px', background: '#eff6ff', color: '#1a56db', border: '1px solid #bfdbfe', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                      {pinVisible ? '🙈 Hide' : '👁 Show PIN'}
                    </button>
                  </div>
                </div>
              )}
              <div style={{ marginTop: 20 }}>
                <button onClick={() => nav('/book')} style={{ padding: '6px 14px', background: '#1e3a5f', color: '#60a5fa', border: '1px solid #1e3a5f', borderRadius: 0, cursor: 'pointer', fontSize: 10, fontWeight: 700 }}>Browse other venues</button>
              </div>
            </Card>
          )}
        </div>
      </div>
      <PortalFooter />
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, { bg: string; text: string }> = {
    confirmed: { bg: '#f0fdf4', text: '#16a34a' },
    approved: { bg: '#f0fdf4', text: '#16a34a' },
    pending: { bg: '#fffbeb', text: '#d97706' },
    cancelled: { bg: '#fef2f2', text: '#dc2626' },
    rejected: { bg: '#fef2f2', text: '#dc2626' },
    completed: { bg: '#f5f3ff', text: '#6366f1' },
  };
  const c = colors[status] || { bg: '#f8fafc', text: '#555555' };
  return <span style={{ background: c.bg, color: c.text, borderRadius: 20, padding: '4px 14px', fontSize: 13, fontWeight: 700, textTransform: 'capitalize' }}>{status}</span>;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function formatDate(s: string) {
  if (!s) return '';
  try {
    return new Date(s + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return s; }
}

function calcCost(start: string, end: string, rate: number): string {
  try {
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const hrs = (eh * 60 + em - sh * 60 - sm) / 60;
    return (hrs * rate).toFixed(2);
  } catch { return '—'; }
}

// ═════════════════════════════════════════════════════════════════════════════
// ROOT EXPORT
// ═════════════════════════════════════════════════════════════════════════════
export default function PublicBookingPortal() {
  return (
    <PubAuthProvider>
      <style>{`
        * { box-sizing: border-box; }
        body { margin: 0; }
        @keyframes spin { to { transform: rotate(360deg); } }
        input[type="date"]::-webkit-calendar-picker-indicator { cursor: pointer; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: #f1f5f9; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
      `}</style>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="venue/:id" element={<VenueDetailPage />} />
        <Route path="new" element={<NewBookingPage />} />
        <Route path="login" element={<LoginPage />} />
        <Route path="register" element={<RegisterPage />} />
        <Route path="my-bookings" element={<MyBookingsPage />} />
        <Route path="lookup" element={<LookupPage />} />
      </Routes>
    </PubAuthProvider>
  );
}
